import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';

interface NetworkConnection {
  effectiveType?: string;
  saveData?: boolean;
  addEventListener?: (type: 'change', listener: () => void) => void;
}

interface NavigatorWithConnection extends Navigator {
  connection?: NetworkConnection;
}

@Injectable({
  providedIn: 'root'
})
export class SelectivePreloadingStrategy implements PreloadingStrategy {
  preloadedModules: string[] = [];
  private networkSpeed = 'fast';
  private isOnSlowConnection = false;

  constructor() {
    this.detectNetworkSpeed();
  }

  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    // Don't preload on slow connections
    if (this.isOnSlowConnection) {
      return of(null);
    }

    // Check if route should be preloaded based on data.preload flag
    if (route.data && route.data['preload']) {
      // Delay preloading to avoid blocking initial load
      const delay = this.getPreloadDelay(route);
      
      return timer(delay).pipe(
        switchMap(() => {
          this.preloadedModules.push(route.path || '');
          return load();
        })
      );
    } else {
      return of(null);
    }
  }

  private getPreloadDelay(route: Route): number {
    // Prioritize critical routes with shorter delays
    const priority = route.data?.['priority'] || 'normal';
    
    switch (priority) {
      case 'high':
        return 100; // Preload almost immediately
      case 'normal':
        return 200; // Preload after 1 second
      case 'low':
        return 200; // Preload after 3 seconds
      default:
        return 200;
    }
  }

  private detectNetworkSpeed(): void {
    const navigatorWithConnection = navigator as NavigatorWithConnection;

    // Use Network Information API if available
    if ('connection' in navigatorWithConnection) {
      const connection = navigatorWithConnection.connection;
      
      if (connection) {
        // Consider 2G/slow-2g as slow connections
        this.isOnSlowConnection = connection.effectiveType === '2g' || 
                                  connection.effectiveType === 'slow-2g';
        this.networkSpeed = connection.effectiveType ?? 'fast';
        
        // Listen for network changes
        connection.addEventListener?.('change', () => {
          this.isOnSlowConnection = connection.effectiveType === '2g' || 
                                    connection.effectiveType === 'slow-2g';
          this.networkSpeed = connection.effectiveType ?? 'fast';
        });
      }
    }

    // Fallback: detect based on save-data header
    if ('connection' in navigatorWithConnection && navigatorWithConnection.connection?.saveData) {
      this.isOnSlowConnection = true;
    }
  }

  // Method to get preloading statistics
  getPreloadingStats(): { preloadedModules: string[], networkSpeed: string, isSlowConnection: boolean } {
    return {
      preloadedModules: this.preloadedModules,
      networkSpeed: this.networkSpeed,
      isSlowConnection: this.isOnSlowConnection
    };
  }
}
